import { BaseService, ServiceError } from './BaseService';
import { 
  SuccessWareImportRepository,
  ImportJob,
  ImportBatch,
  CreateImportJobParams,
} from '../repositories/SuccessWareImportRepository';

/**
 * Entity types that can be imported from SuccessWare
 * Ordered by dependency (entities with no dependencies first)
 */
export const ENTITY_TYPES = [
  'employees',
  'customers',
  'service_accounts',
  'locations',
  'equipment',
  'jobs',
  'assignments',
  'agreements',
  'invoices',
  'billing_customers',
] as const;

export type EntityType = typeof ENTITY_TYPES[number];

/**
 * Import progress information
 */
export interface ImportProgress {
  job: ImportJob;
  batchStats: {
    total_batches: number;
    completed_batches: number;
    failed_batches: number;
    total_records: number;
    successful_records: number;
    failed_records: number;
  };
  percentComplete: number;
  estimatedTimeRemaining: string | null;
}

/**
 * Full import configuration
 */
export interface FullImportConfig {
  company_id: string;
  entity_types?: EntityType[];
  batch_size?: number;
  delay_between_batches?: number;
}

/**
 * Service for managing SuccessWare imports
 * 
 * This service coordinates the import process for SuccessWare data:
 * - Creates and manages import jobs for different entity types
 * - Handles pause/resume functionality (state persisted in database)
 * - Processes data in batches to avoid overwhelming the server
 * - Tracks progress and statistics
 * - Schedules batch processing via Edge Functions
 * 
 * Key features:
 * - Pausable/resumable: Imports survive computer shutdown
 * - Batch processing: Default 100 records per batch
 * - Progress tracking: Real-time updates via database
 * - Error handling: Automatic retry with exponential backoff
 * - Multi-entity: Import 10 different entity types in order
 * 
 * @example
 * ```typescript
 * const service = new SuccessWareImportService();
 * 
 * // Start a full import for a company
 * const jobs = await service.startFullImport({
 *   company_id: 'company-123',
 *   batch_size: 100
 * });
 * 
 * // Get progress for a specific job
 * const progress = await service.getImportProgress(jobs[0].id);
 * console.log(`${progress.percentComplete}% complete`);
 * 
 * // Pause an import
 * await service.pauseImport(jobs[0].id);
 * 
 * // Resume later (even after restart)
 * await service.resumeImport(jobs[0].id);
 * ```
 */
export class SuccessWareImportService extends BaseService {
  private repository: SuccessWareImportRepository;

  constructor() {
    super();
    this.repository = new SuccessWareImportRepository();
  }

  /**
   * Start a full import for a company (all entity types)
   * 
   * Creates import jobs for each entity type in dependency order.
   * Jobs are created in 'pending' status and must be processed by
   * the batch processor Edge Function.
   * 
   * @param config Import configuration
   * @returns Promise resolving to array of created jobs
   */
  async startFullImport(config: FullImportConfig): Promise<ImportJob[]> {
    try {
      this.requireFields(config, ['company_id']);

      const entity_types = config.entity_types || ENTITY_TYPES;
      const batch_size = config.batch_size || 100;

      // Create a job for each entity type
      const jobs: ImportJob[] = [];
      
      for (const entity_type of entity_types) {
        const jobParams: CreateImportJobParams = {
          company_id: config.company_id,
          job_type: entity_type,
          batch_size,
          total_pages: null, // Will be determined during first fetch
        };

        const job = await this.repository.createImportJob(jobParams);
        jobs.push(job);

        await this.emit('import.job.created', {
          job_id: job.id,
          company_id: config.company_id,
          entity_type,
        });
      }

      // Emit event to trigger processing
      await this.emit('import.started', {
        company_id: config.company_id,
        job_ids: jobs.map(j => j.id),
        entity_types,
      });

      return jobs;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Start a single entity import
   * 
   * @param company_id Company ID
   * @param entity_type Entity type to import
   * @param batch_size Batch size (default 100)
   * @returns Promise resolving to the created job
   */
  async startEntityImport(
    company_id: string,
    entity_type: EntityType,
    batch_size: number = 100
  ): Promise<ImportJob> {
    try {
      this.requireFields({ company_id, entity_type }, ['company_id', 'entity_type']);

      const job = await this.repository.createImportJob({
        company_id,
        job_type: entity_type,
        batch_size,
      });

      await this.emit('import.job.created', {
        job_id: job.id,
        company_id,
        entity_type,
      });

      // Emit event to trigger processing
      await this.emit('import.job.ready', {
        job_id: job.id,
        company_id,
        entity_type,
      });

      return job;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Pause a running import
   * 
   * Saves the current state to the database so it can be resumed later,
   * even after computer shutdown. The current_page field maintains position.
   * 
   * @param job_id Job ID to pause
   * @returns Promise resolving to the paused job
   */
  async pauseImport(job_id: string): Promise<ImportJob> {
    try {
      this.requireFields({ job_id }, ['job_id']);

      const job = await this.repository.getImportJob(job_id);
      if (!job) {
        throw ServiceError.notFound('Import job', job_id);
      }

      if (job.status !== 'running' && job.status !== 'pending') {
        throw ServiceError.conflict(
          `Cannot pause job in ${job.status} status. Only running or pending jobs can be paused.`
        );
      }

      const pausedJob = await this.repository.pauseImportJob(job_id);

      await this.emit('import.paused', {
        job_id,
        company_id: job.company_id,
        entity_type: job.job_type,
        current_page: job.current_page,
      });

      return pausedJob;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Resume a paused import
   * 
   * Loads the saved state from the database and continues from where it left off.
   * Works even after computer shutdown because state is persisted in database.
   * 
   * @param job_id Job ID to resume
   * @returns Promise resolving to the resumed job
   */
  async resumeImport(job_id: string): Promise<ImportJob> {
    try {
      this.requireFields({ job_id }, ['job_id']);

      const job = await this.repository.getImportJob(job_id);
      if (!job) {
        throw ServiceError.notFound('Import job', job_id);
      }

      if (job.status !== 'paused') {
        throw ServiceError.conflict(
          `Cannot resume job in ${job.status} status. Only paused jobs can be resumed.`
        );
      }

      const resumedJob = await this.repository.resumeImportJob(job_id);

      await this.emit('import.resumed', {
        job_id,
        company_id: job.company_id,
        entity_type: job.job_type,
        current_page: job.current_page,
      });

      // Emit event to trigger processing from current page
      await this.emit('import.job.ready', {
        job_id,
        company_id: job.company_id,
        entity_type: job.job_type,
        resume_from_page: job.current_page,
      });

      return resumedJob;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Cancel an import
   * 
   * @param job_id Job ID to cancel
   * @returns Promise resolving to the cancelled job
   */
  async cancelImport(job_id: string): Promise<ImportJob> {
    try {
      this.requireFields({ job_id }, ['job_id']);

      const job = await this.repository.getImportJob(job_id);
      if (!job) {
        throw ServiceError.notFound('Import job', job_id);
      }

      if (job.status === 'completed' || job.status === 'cancelled') {
        throw ServiceError.conflict(
          `Cannot cancel job in ${job.status} status.`
        );
      }

      const cancelledJob = await this.repository.cancelImportJob(job_id);

      await this.emit('import.cancelled', {
        job_id,
        company_id: job.company_id,
        entity_type: job.job_type,
      });

      return cancelledJob;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get detailed progress for an import job
   * 
   * @param job_id Job ID
   * @returns Promise resolving to progress information
   */
  async getImportProgress(job_id: string): Promise<ImportProgress> {
    try {
      this.requireFields({ job_id }, ['job_id']);

      const job = await this.repository.getImportJob(job_id);
      if (!job) {
        throw ServiceError.notFound('Import job', job_id);
      }

      const batchStats = await this.repository.getBatchStats(
        job.company_id,
        job.job_type
      );

      // Calculate percent complete
      let percentComplete = 0;
      if (job.total_pages && job.total_pages > 0) {
        percentComplete = Math.round((job.current_page / job.total_pages) * 100);
      } else if (batchStats.total_records > 0) {
        const processedRecords = batchStats.successful_records + batchStats.failed_records;
        percentComplete = Math.round((processedRecords / batchStats.total_records) * 100);
      }

      // Estimate time remaining (rough calculation)
      let estimatedTimeRemaining: string | null = null;
      if (job.started_at && job.total_pages && job.current_page > 0) {
        const elapsed = Date.now() - new Date(job.started_at).getTime();
        const avgTimePerPage = elapsed / job.current_page;
        const pagesRemaining = job.total_pages - job.current_page;
        const msRemaining = avgTimePerPage * pagesRemaining;
        
        const minutes = Math.ceil(msRemaining / 60000);
        if (minutes < 60) {
          estimatedTimeRemaining = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
        } else {
          const hours = Math.floor(minutes / 60);
          const remainingMinutes = minutes % 60;
          estimatedTimeRemaining = `${hours} hour${hours !== 1 ? 's' : ''}${
            remainingMinutes > 0 ? ` ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}` : ''
          }`;
        }
      }

      return {
        job,
        batchStats,
        percentComplete,
        estimatedTimeRemaining,
      };
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get all import jobs for a company
   * 
   * @param company_id Company ID
   * @returns Promise resolving to array of jobs
   */
  async getCompanyImports(company_id: string): Promise<ImportJob[]> {
    try {
      this.requireFields({ company_id }, ['company_id']);

      return await this.repository.getJobsByCompany(company_id, {
        orderBy: { column: 'created_at', ascending: false },
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get all active import jobs
   * 
   * @param company_id Optional company filter
   * @returns Promise resolving to array of active jobs
   */
  async getActiveImports(company_id?: string): Promise<ImportJob[]> {
    try {
      return await this.repository.getActiveImportJobs(company_id);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get progress for all active imports
   * 
   * @param company_id Optional company filter
   * @returns Promise resolving to array of progress information
   */
  async getActiveImportsProgress(company_id?: string): Promise<ImportProgress[]> {
    try {
      const activeJobs = await this.getActiveImports(company_id);
      
      const progressPromises = activeJobs.map(job =>
        this.getImportProgress(job.id)
      );

      return await Promise.all(progressPromises);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Schedule the next batch for processing
   * 
   * This method is called by the batch processor to advance through pages.
   * It updates the job's current_page and emits an event to trigger the
   * next batch processing.
   * 
   * @param job_id Job ID
   * @param next_page Next page number to process
   * @param total_pages Total pages (if now known)
   * @returns Promise resolving to the updated job
   */
  async scheduleNextBatch(
    job_id: string,
    next_page: number,
    total_pages?: number
  ): Promise<ImportJob> {
    try {
      this.requireFields({ job_id, next_page }, ['job_id', 'next_page']);

      const updates: any = {
        current_page: next_page,
        status: 'running' as const,
        started_at: new Date().toISOString(),
      };

      if (total_pages !== undefined) {
        updates.total_pages = total_pages;
      }

      const job = await this.repository.updateImportJob(job_id, updates);

      // Emit event to trigger next batch processing
      await this.emit('import.batch.ready', {
        job_id,
        company_id: job.company_id,
        entity_type: job.job_type,
        page: next_page,
      });

      return job;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Mark a job as completed
   * 
   * @param job_id Job ID
   * @returns Promise resolving to the completed job
   */
  async completeImport(job_id: string): Promise<ImportJob> {
    try {
      this.requireFields({ job_id }, ['job_id']);

      const job = await this.repository.getImportJob(job_id);
      if (!job) {
        throw ServiceError.notFound('Import job', job_id);
      }

      const completedJob = await this.repository.completeImportJob(job_id);

      await this.emit('import.completed', {
        job_id,
        company_id: job.company_id,
        entity_type: job.job_type,
        records_imported: job.records_imported,
        records_failed: job.records_failed,
      });

      return completedJob;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Record a batch result and update job statistics
   * 
   * @param job_id Job ID
   * @param batch Batch information
   * @returns Promise resolving to void
   */
  async recordBatchResult(
    job_id: string,
    batch: {
      successful: number;
      failed: number;
      errors?: any[];
    }
  ): Promise<void> {
    try {
      this.requireFields({ job_id }, ['job_id']);

      // Update job statistics
      if (batch.successful > 0) {
        await this.repository.incrementImportedRecords(job_id, batch.successful);
      }

      if (batch.failed > 0) {
        await this.repository.incrementFailedRecords(job_id, batch.failed);
      }

      // Create batch record for tracking
      const job = await this.repository.getImportJob(job_id);
      if (job) {
        await this.repository.createBatch({
          company_id: job.company_id,
          import_type: job.job_type,
          batch_size: batch.successful + batch.failed,
          records_total: batch.successful + batch.failed,
        });
      }

      await this.emit('import.batch.completed', {
        job_id,
        successful: batch.successful,
        failed: batch.failed,
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Check if a company has any active imports
   * 
   * @param company_id Company ID
   * @returns Promise resolving to boolean
   */
  async hasActiveImports(company_id: string): Promise<boolean> {
    try {
      this.requireFields({ company_id }, ['company_id']);

      const activeJobs = await this.repository.getActiveImportJobs(company_id);
      return activeJobs.length > 0;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get import summary for a company
   * 
   * @param company_id Company ID
   * @returns Promise resolving to import summary
   */
  async getImportSummary(company_id: string): Promise<{
    total_jobs: number;
    active_jobs: number;
    completed_jobs: number;
    failed_jobs: number;
    total_records_imported: number;
    total_records_failed: number;
  }> {
    try {
      this.requireFields({ company_id }, ['company_id']);

      const jobs = await this.repository.getJobsByCompany(company_id);

      return {
        total_jobs: jobs.length,
        active_jobs: jobs.filter(j => j.status === 'running' || j.status === 'pending').length,
        completed_jobs: jobs.filter(j => j.status === 'completed').length,
        failed_jobs: jobs.filter(j => j.status === 'failed').length,
        total_records_imported: jobs.reduce((sum, j) => sum + j.records_imported, 0),
        total_records_failed: jobs.reduce((sum, j) => sum + j.records_failed, 0),
      };
    } catch (error) {
      this.handleError(error);
    }
  }
}
